import { Component, ElementRef, Inject, NgZone, OnDestroy, Optional, ViewChild } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { ConnectedPosition } from "@angular/cdk/overlay";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { firstValueFrom } from "rxjs";
import { ApiService } from "src/app/services/api.service";
import { CallToneService } from "src/app/services/call-tone.service";
import { ChimeMeetingService, PresenceUpdate, TranscriptUpdate } from "src/app/services/chime-meeting.service";
import {
  CallChip,
  CallView,
  ClickToCallData,
  CopyState,
  PhoneCall,
  PhoneCallStatus,
  PhoneCallStatusResponse,
  TranscriptLine,
  TranscriptState,
} from "./click-to-call.model";
import {
  DEFAULT_COUNTRY,
  PHONE_COUNTRIES,
  PhoneCountry,
  formatInternational,
  formatNational,
  nationalDigits,
  parsePhoneNumber,
  phoneProblem,
  placeholderFor,
  ruleProblem,
  toE164,
} from "./phone-number";
import { redactPhi } from "../meeting-room/phi-redact";
import {
  formatClock,
  settleTranscript,
  speakerLabel,
  transcriptFileName,
  transcriptText,
  upsertTranscriptLine,
} from "./call-transcript";

/** No answer within this long after the call is placed ends it. */
const RING_TIMEOUT_MS = 30000;
const RING_CYCLE_MS = 6000;
const STATUS_POLL_MS = 3000;
/** Consecutive failed status polls before the panel warns about the connection. */
const POLL_FAILURES_BEFORE_WARNING = 3;
const DEFAULT_MAX_DURATION_S = 3600;
/** How long a connected call may go without a transcript line before the panel says so. */
const TRANSCRIPT_QUIET_MS = 20000;
const CLIPBOARD_WAIT_MS = 1500;

const TRANSCRIPT_STATE_LABEL: Record<TranscriptState, string> = {
  waiting: "Waiting",
  live: "Live",
  unavailable: "Off",
  error: "Stopped",
};

const END_CHIPS: Partial<Record<PhoneCallStatus, CallChip>> = {
  cancelled: { label: "Cancelled", tone: "neutral" },
  "no-answer": { label: "No answer", tone: "neutral" },
  failed: { label: "Call failed", tone: "danger" },
  dropped: { label: "Call dropped", tone: "danger" },
};

/**
 * Outgoing phone call panel: enter number → connecting → ringing → on call → call ended.
 *
 * Real telephony only: the call is placed by `api/meetings/calls/start`, its audio runs through
 * an audio-only Chime session (ChimeMeetingService), the status is polled until it ends, and the
 * live transcript is the Chime transcription stream of that session. Ringback and hang-up tones
 * are local cues (CallToneService). There is no inbound-call API, so the panel only dials out.
 * Open it with `ClickToCallComponent.open(dialog, data)` so every caller gets the same panel.
 */
@Component({
  selector: "app-click-to-call",
  templateUrl: "./click-to-call.component.html",
  styleUrls: ["./click-to-call.component.scss"],
  standalone: false,
})
export class ClickToCallComponent implements OnDestroy {
  static open(dialog: MatDialog, data: ClickToCallData = {}): MatDialogRef<ClickToCallComponent> {
    return dialog.open(ClickToCallComponent, {
      width: "400px",
      maxWidth: "calc(100vw - 24px)",
      maxHeight: "calc(100dvh - 24px)",
      autoFocus: "#ctc-number",
      disableClose: true,
      panelClass: "ctc-dialog",
      data,
    });
  }

  @ViewChild("callAudio") callAudio?: ElementRef<HTMLAudioElement>;
  @ViewChild("numberInput") numberInput?: ElementRef<HTMLInputElement>;
  @ViewChild("transcriptList") transcriptList?: ElementRef<HTMLDivElement>;

  readonly countries = PHONE_COUNTRIES;
  /** The country menu opens under the number field, left-aligned, or above it when there is no room. */
  readonly countryMenuPosition: ConnectedPosition[] = [
    { originX: "start", originY: "bottom", overlayX: "start", overlayY: "top", offsetY: 6 },
    { originX: "start", originY: "top", overlayX: "start", overlayY: "bottom", offsetY: -6 },
  ];
  readonly title: string;
  readonly contactName: string;

  // Number entry
  country: PhoneCountry = DEFAULT_COUNTRY;
  digits = "";
  touched = false;

  // Call
  call: PhoneCall | null = null;
  status: PhoneCallStatus | "" = "";
  starting = false;
  finished = false;
  audioReady = false;
  muted = false;
  audioBlocked = false;
  error = "";
  warning = "";
  now = Date.now();

  // Transcript
  lines: TranscriptLine[] = [];
  transcriptState: TranscriptState = "waiting";
  copyState: CopyState = "idle";

  private placedAt = 0;
  private ringingSince = 0;
  private answeredAt = 0;
  private endedAt = 0;
  private transcriptMessage = "";
  /** Keep the newest transcript line in view until the user scrolls up to read back. */
  private followNewest = true;
  private pollFailures = 0;
  /** Bumped per call, so callbacks from an earlier call's session are ignored. */
  private attempt = 0;
  private destroyed = false;
  private ticker?: ReturnType<typeof setInterval>;
  private pollTimer?: ReturnType<typeof setInterval>;
  private ringTimer?: ReturnType<typeof setTimeout>;
  private durationTimer?: ReturnType<typeof setTimeout>;
  private copyTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private api: ApiService,
    private chime: ChimeMeetingService,
    private tones: CallToneService,
    private dialogRef: MatDialogRef<ClickToCallComponent>,
    private zone: NgZone,
    @Optional() @Inject(MAT_DIALOG_DATA) data: ClickToCallData | null
  ) {
    this.title = data?.title || "Phone call";
    this.contactName = (data?.contactName || "").trim();
    if (data?.phoneNumber) {
      const parsed = parsePhoneNumber(data.phoneNumber);
      this.country = parsed.country;
      this.digits = parsed.digits;
    }
  }

  // ── View state ──────────────────────────────────────────────────────────

  get view(): CallView {
    if (this.finished) return "ended";
    if (this.answeredAt) return "connected";
    if (this.ringingSince) return "ringing";
    if (this.starting || this.call) return "connecting";
    return "enter";
  }

  get live(): boolean {
    return this.view === "connecting" || this.view === "ringing" || this.view === "connected";
  }

  get chip(): CallChip {
    switch (this.view) {
      case "connecting":
        return { label: "Connecting", tone: "info" };
      case "ringing":
        return { label: "Ringing", tone: "info" };
      case "connected":
        return { label: "Connected", tone: "success" };
      default:
        return END_CHIPS[this.status as PhoneCallStatus] ?? { label: "Call ended", tone: "neutral" };
    }
  }

  /** "Ring 3 · 00:14" while ringing. */
  get ringLabel(): string {
    const ms = this.now - this.ringingSince;
    return `Ring ${Math.floor(ms / RING_CYCLE_MS) + 1} · ${formatClock(ms)}`;
  }

  /** Talk time: runs from the answer, frozen at the end. */
  get duration(): string {
    if (!this.answeredAt) return "";
    return formatClock((this.endedAt || this.now) - this.answeredAt);
  }

  get showTranscript(): boolean {
    return this.answeredAt > 0;
  }

  get customerLabel(): string {
    return this.contactName || "Customer";
  }

  get transcriptNotice(): string {
    if (this.transcriptState === "unavailable" || this.transcriptState === "error") return this.transcriptMessage;
    if (this.lines.length) return "";
    if (this.finished) return "No speech was transcribed during this call.";
    return this.now - this.answeredAt > TRANSCRIPT_QUIET_MS
      ? "Nothing transcribed yet. Live transcription may not be running for this call."
      : "Listening… what's said on the call appears here.";
  }

  get transcriptStateLabel(): string {
    return TRANSCRIPT_STATE_LABEL[this.transcriptState];
  }

  get copyLabel(): string {
    if (this.copyState === "copied") return "Copied";
    if (this.copyState === "failed") return "Copy failed";
    return "Copy";
  }

  speakerOf(line: TranscriptLine): string {
    return speakerLabel(line.speaker, this.customerLabel);
  }

  clockOf(line: TranscriptLine): string {
    return formatClock(line.atMs);
  }

  trackByLine(_: number, line: TranscriptLine): string {
    return line.id;
  }

  onTranscriptScroll(): void {
    const el = this.transcriptList?.nativeElement;
    if (el) this.followNewest = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  // ── Number entry ────────────────────────────────────────────────────────

  get nationalLabel(): string {
    return formatNational(this.country, this.digits);
  }

  get numberLabel(): string {
    return formatInternational(this.country, this.digits);
  }

  get placeholder(): string {
    return placeholderFor(this.country);
  }

  get canCall(): boolean {
    return !phoneProblem(this.country, this.digits) && !this.starting;
  }

  /** Rule problems show while typing; "too short" waits until the field is left. */
  get validationMessage(): string {
    const rule = ruleProblem(this.country, this.digits);
    if (rule) return rule;
    return this.touched && this.digits ? phoneProblem(this.country, this.digits) : "";
  }

  get numberHint(): string {
    return `Enter the ${this.country.length}-digit ${this.country.name} number, without +${this.country.dial}.`;
  }

  get initials(): string {
    const words = this.contactName.split(/\s+/).filter(Boolean);
    if (!words.length) return "";
    const last = words.length > 1 ? words[words.length - 1].charAt(0) : "";
    return (words[0].charAt(0) + last).toUpperCase();
  }

  /** With a contact the number sits under the name; without one the number is the headline. */
  get subLine(): string {
    if (!this.contactName) return "";
    return this.view === "connecting" || this.view === "ringing" ? `Calling ${this.numberLabel}` : this.numberLabel;
  }

  /** Picked from the country menu: keep what fits the new country, then back to typing the number. */
  selectCountry(country: PhoneCountry): void {
    this.country = country;
    this.digits = this.digits.slice(0, country.length);
    this.error = "";
    // After the menu has closed and returned focus to its trigger.
    setTimeout(() => this.numberInput?.nativeElement.focus());
  }

  /** Keeps national digits only and rewrites the field in the country's format as the user types or pastes. */
  onNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.digits = nationalDigits(this.country, input.value);
    input.value = this.nationalLabel;
    this.error = "";
  }

  // ── Call ────────────────────────────────────────────────────────────────

  async start(): Promise<void> {
    // Repeated clicks: one call at a time.
    if (this.starting || this.call) return;
    this.touched = true;
    if (!this.canCall) return;
    // Inside the click, before any await: browsers allow sound only from a user gesture.
    this.tones.prime();
    const audio = this.callAudio?.nativeElement;
    if (audio) void audio.play().catch(() => undefined);

    this.resetCall();
    const attempt = ++this.attempt;
    this.starting = true;
    this.startTicker();
    try {
      const micProblem = await this.microphoneProblem();
      if (attempt !== this.attempt || this.finished || this.destroyed) return;
      if (micProblem) {
        this.error = micProblem;
        this.stopTimers();
        return;
      }

      const call = (await firstValueFrom(this.api.startPhoneCall({ toPhoneNumber: toE164(this.country, this.digits) }))) as PhoneCall;
      this.call = call;
      this.placedAt = Date.now();
      // Cancelled or closed while the call was being placed: hang it up at once.
      if (attempt !== this.attempt || this.finished || this.destroyed) {
        await this.teardown(call);
        return;
      }
      this.applyTranscriptionInfo(call);
      this.startCallTimers(call);
      this.onStatus(call.status || "dialing");
      void this.refreshStatus();

      await this.chime.join(
        call.meetingResponse,
        call.attendeeResponse,
        audio as HTMLAudioElement,
        {
          audioVideoDidStart: () => this.zone.run(() => void this.enableSound()),
          audioVideoDidStop: () => this.zone.run(() => this.onAudioStopped(attempt)),
        },
        true,
        (update: TranscriptUpdate) => this.zone.run(() => this.onTranscript(attempt, update)),
        (update: PresenceUpdate) => this.onPresence(attempt, update)
      );
      if (attempt !== this.attempt || this.finished || this.destroyed) {
        await this.chime.leave();
        return;
      }
      this.audioReady = true;
      await this.enableSound();
      if (!this.chime.microphoneReady) this.warning = "Your microphone isn't available, so the other side can't hear you.";
    } catch (err: unknown) {
      if (attempt !== this.attempt) return;
      this.error = errorMessage(err, "The call could not be started.");
      if (this.call?.callKey) {
        await this.endCall("failed");
      } else if (!this.finished) {
        // Nothing was placed: back to the number, with the reason.
        this.stopTimers();
        this.tones.stopAll();
        this.call = null;
        this.status = "";
        this.ringingSince = 0;
        void this.chime.leave();
      }
    } finally {
      if (attempt === this.attempt) this.starting = false;
    }
  }

  async enableSound(): Promise<void> {
    const audio = this.callAudio?.nativeElement;
    if (!audio) return;
    audio.muted = false;
    try {
      await audio.play();
      this.audioBlocked = false;
    } catch {
      this.audioBlocked = true;
    }
  }

  async toggleMute(): Promise<void> {
    if (!this.audioReady || this.finished) return;
    this.muted = !this.muted;
    await this.chime.toggleMute(this.muted);
  }

  /** Red button: "Cancel" before an answer, "End call" after. Either way, straight to the ended panel. */
  hangUp(): void {
    void this.endCall(this.answeredAt ? "ended" : "cancelled");
  }

  /** Redials the same number with a clean slate. */
  callAgain(): void {
    if (this.starting) return;
    this.call = null;
    void this.start();
  }

  close(): void {
    this.dialogRef.close();
  }

  // ── Transcript export ───────────────────────────────────────────────────

  copyTranscript(): void {
    const text = this.transcriptAsText();
    // The Clipboard API's native promise settles outside Angular's zone; re-enter it to redraw.
    const done = (ok: boolean) =>
      this.zone.run(() => {
        this.copyState = ok ? "copied" : "failed";
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => (this.copyState = "idle"), 2500);
      });
    if (!navigator.clipboard?.writeText) {
      done(copyWithTextarea(text));
      return;
    }
    // Some browsers leave the request pending (unfocused page, no permission); don't wait forever.
    const settled = navigator.clipboard.writeText(text).then(
      () => true,
      () => false
    );
    const timedOut = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), CLIPBOARD_WAIT_MS));
    void Promise.race([settled, timedOut]).then((ok) => done(ok || copyWithTextarea(text)));
  }

  downloadTranscript(): void {
    const blob = new Blob([this.transcriptAsText()], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = transcriptFileName(new Date(this.placedAt || Date.now()));
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearTimeout(this.copyTimer);
    this.tones.stopAll();
    if (this.call || this.starting) void this.endCall("ended");
    this.stopTimers();
  }

  // ── Internals ───────────────────────────────────────────────────────────

  /** Everything a new call must not inherit from the last one: timers, sounds, state, transcript. */
  private resetCall(): void {
    this.stopTimers();
    this.tones.stopAll();
    this.call = null;
    this.status = "";
    this.finished = false;
    this.audioReady = false;
    this.muted = false;
    this.audioBlocked = false;
    this.error = "";
    this.warning = "";
    this.placedAt = 0;
    this.ringingSince = 0;
    this.answeredAt = 0;
    this.endedAt = 0;
    this.pollFailures = 0;
    this.lines = [];
    this.transcriptState = "waiting";
    this.transcriptMessage = "";
    this.followNewest = true;
    this.copyState = "idle";
  }

  /** "" when a call can use the microphone, otherwise what to tell the user. */
  private async microphoneProblem(): Promise<string> {
    if (!this.chime.isBrowserSupported()) {
      return "This browser can't place calls. Use a current Chrome, Edge, Firefox or Safari, over HTTPS.";
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return "";
    } catch (err: unknown) {
      const name = (err as { name?: string } | null)?.name;
      if (name === "NotAllowedError" || name === "SecurityError") {
        return "Microphone access is blocked. Allow it in the browser's site settings, then call again.";
      }
      if (name === "NotFoundError") return "No microphone was found. Connect one, then call again.";
      return "The microphone couldn't be started. Close other apps using it, then call again.";
    }
  }

  private onStatus(status: PhoneCallStatus): void {
    if (this.finished) return;
    if (status === "answered") {
      this.markAnswered();
      return;
    }
    if (this.answeredAt) return;
    this.status = status;
    if (status === "ringing" && !this.ringingSince) {
      this.ringingSince = Date.now();
      this.tones.startRingback();
    }
  }

  private markAnswered(): void {
    if (this.answeredAt || this.finished) return;
    this.answeredAt = Date.now();
    this.status = "answered";
    this.tones.stopRingback();
    clearTimeout(this.ringTimer);
  }

  private async endCall(status: PhoneCallStatus): Promise<void> {
    if (this.finished) return;
    this.finished = true;
    this.status = status;
    this.endedAt = Date.now();
    this.now = this.endedAt;
    this.stopTimers();
    this.lines = settleTranscript(this.lines);
    if (this.destroyed) this.tones.stopAll();
    else this.tones.playDisconnect();
    // While the call is still being placed there is nothing to hang up yet; start() does it on arrival.
    if (this.call) await this.teardown(this.call);
  }

  private async teardown(call: PhoneCall): Promise<void> {
    if (call.callKey) {
      try {
        await firstValueFrom(this.api.hangupPhoneCall({ callKey: call.callKey }));
      } catch {
        // The phone network ends a call the browser has already left.
      }
    }
    await this.chime.leave();
  }

  private startTicker(): void {
    this.now = Date.now();
    this.ticker = setInterval(() => (this.now = Date.now()), 1000);
  }

  private startCallTimers(call: PhoneCall): void {
    this.pollTimer = setInterval(() => void this.refreshStatus(), STATUS_POLL_MS);
    this.ringTimer = setTimeout(() => {
      if (!this.answeredAt) void this.endCall("no-answer");
    }, RING_TIMEOUT_MS);
    this.durationTimer = setTimeout(() => void this.endCall("ended"), (call.maxDurationSeconds || DEFAULT_MAX_DURATION_S) * 1000);
  }

  private stopTimers(): void {
    clearInterval(this.ticker);
    clearInterval(this.pollTimer);
    clearTimeout(this.ringTimer);
    clearTimeout(this.durationTimer);
  }

  private onPresence(attempt: number, update: PresenceUpdate): void {
    if (attempt !== this.attempt || !update.attendeeId || update.attendeeId === this.chime.localAttendeeId()) return;
    this.zone.run(() => {
      if (update.present) this.markAnswered();
      else if (this.answeredAt) void this.endCall("ended");
    });
  }

  /** The call's audio session stopped without us hanging up: the call dropped. */
  private onAudioStopped(attempt: number): void {
    if (attempt !== this.attempt || this.finished || !this.call) return;
    this.error = "The call audio was disconnected.";
    void this.endCall("dropped");
  }

  private applyTranscriptionInfo(call: PhoneCall): void {
    if (call.transcriptionEnabled === false) {
      this.transcriptState = "unavailable";
      this.transcriptMessage = call.transcriptionError || "Live transcription isn't turned on for phone calls.";
    }
  }

  private onTranscript(attempt: number, update: TranscriptUpdate): void {
    if (attempt !== this.attempt || this.finished) return;
    if (update.failed) {
      this.transcriptState = "error";
      this.transcriptMessage = update.statusMessage || "Transcription stopped.";
      return;
    }
    if (!update.text) return;
    const local = this.chime.localExternalUserId();
    const speaker = update.speakerId ? (update.speakerId === local ? "agent" : "customer") : "unknown";
    const base = this.answeredAt || this.placedAt || Date.now();
    // Chime sends epoch milliseconds; anything else is placed at the moment it arrived.
    const at = update.startTimeMs > 1e12 ? update.startTimeMs : Date.now();
    this.lines = upsertTranscriptLine(this.lines, {
      id: update.resultId,
      speaker,
      text: redactPhi(update.text),
      partial: update.partial,
      atMs: Math.max(0, at - base),
    });
    if (this.transcriptState === "waiting") this.transcriptState = "live";
    this.scrollTranscript();
  }

  /** After the new line has rendered, unless the user is reading further up. */
  private scrollTranscript(): void {
    if (!this.followNewest) return;
    setTimeout(() => {
      const el = this.transcriptList?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  private async refreshStatus(): Promise<void> {
    const callKey = this.call?.callKey;
    if (!callKey || this.finished) return;
    try {
      const latest = (await firstValueFrom(this.api.phoneCallStatus({ callKey }))) as PhoneCallStatusResponse;
      this.pollFailures = 0;
      if (this.warning.startsWith("Connection")) this.warning = "";
      if (this.finished || !latest.status) return;
      if (latest.status === "ended" || latest.status === "failed" || latest.status === "no-answer") {
        await this.endCall(this.answeredAt || latest.status === "failed" ? latest.status : "no-answer");
      } else {
        this.onStatus(latest.status);
      }
    } catch {
      // A missed poll is retried; several in a row get a warning, and the call carries on.
      if (++this.pollFailures >= POLL_FAILURES_BEFORE_WARNING && !this.finished) {
        this.warning = "Connection to the call service is unstable. The call continues.";
      }
    }
  }

  private transcriptAsText(): string {
    return transcriptText(
      {
        title: this.title,
        contact: this.contactName,
        number: this.numberLabel,
        startedAt: new Date(this.placedAt || Date.now()),
        duration: this.duration || "Not connected",
        status: this.chip.label,
      },
      this.lines,
      this.customerLabel
    );
  }
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof HttpErrorResponse && err.status === 0) {
    return "Can't reach the call service. Check your connection and try again.";
  }
  const message = (err as { error?: { message?: unknown } } | null)?.error?.message;
  return typeof message === "string" && message ? message : fallback;
}

/** Clipboard fallback for browsers without the async Clipboard API (or when it is denied). */
function copyWithTextarea(text: string): boolean {
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  area.remove();
  return ok;
}
